/**
 * TEAMCHAT.TSX - Real-time Team Chat Component
 * Hybrid ephemeral (24h) + immortal (permanent) messaging
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTeamChat } from '../hooks';

interface TeamChatProps {
  team: number;
  playerAddress?: string;
  playerName?: string;
  isOpen?: boolean;
  onClose?: () => void;
  signMessage?: (msg: string) => Promise<string>;
  inline?: boolean;
}

// Faction info (THALIA narrative)
const FACTION_INFO = {
  0: { name: 'Viper', emoji: '🐍', color: 'from-green-500 to-emerald-500', bg: 'bg-green-500/10' },
  1: { name: 'Raven', emoji: '🐦‍⬛', color: 'from-cyan-500 to-blue-500', bg: 'bg-cyan-500/10' },
  2: { name: 'Ape', emoji: '🦍', color: 'from-red-500 to-orange-500', bg: 'bg-red-500/10' },
  3: { name: 'Moth', emoji: '🦋', color: 'from-yellow-500 to-amber-500', bg: 'bg-yellow-500/10' },
  4: { name: 'Owl', emoji: '🦉', color: 'from-gray-400 to-white', bg: 'bg-gray-500/10' },
  5: { name: 'Wolf', emoji: '🐺', color: 'from-teal-500 to-cyan-500', bg: 'bg-teal-500/10' },
  6: { name: 'Rabbit', emoji: '🐰', color: 'from-purple-500 to-pink-500', bg: 'bg-purple-500/10' },
  7: { name: 'Fox', emoji: '🦊', color: 'from-pink-500 to-rose-500', bg: 'bg-pink-500/10' },
};
const TEAM_INFO = FACTION_INFO; // Legacy alias

export const TeamChat: React.FC<TeamChatProps> = ({
  team,
  playerAddress,
  playerName,
  isOpen = true,
  onClose,
  signMessage,
  inline = false,
}) => {
  const { messages, send, immortalize, connect, disconnect, connected, error } = useTeamChat({
    teamId: team,
    userAddress: playerAddress,
    userName: playerName,
  });
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const teamInfo = TEAM_INFO[team as keyof typeof TEAM_INFO] || TEAM_INFO[0];

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Connect when opened, disconnect when closed
  useEffect(() => {
    if (isOpen && playerAddress) {
      connect();
    }
    return () => {
      if (!isOpen) disconnect();
    };
  }, [isOpen, playerAddress, connect, disconnect]);

  const handleSend = async () => {
    if (!newMessage.trim() || !playerAddress || isSending) return;

    setIsSending(true);
    try {
      await send(newMessage.trim());
      setNewMessage('');
      inputRef.current?.focus();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleImmortalize = async (content: string) => {
    if (!signMessage) return;
    try {
      await immortalize(content, signMessage);
    } catch (err) {
      console.error('Failed to immortalize message:', err);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatAddress = (addr: string) => {
    if (!addr) return 'Unknown';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 60000) return 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
      <div className="w-full">
        <div className="glass-panel-3d overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`p-3 bg-gradient-to-r ${teamInfo.color} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{teamInfo.emoji}</span>
            <div>
              <h3 className="font-bold text-white text-sm">Team {teamInfo.name}</h3>
              <p className="text-xs text-white/70">
                {connected ? '🟢 Connected' : '🔴 Disconnected'}
              </p>
            </div>
          </div>
          {!inline && onClose && (
            <button
              type="button"
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
              className="text-white/70 hover:text-white transition-colors p-1 cursor-pointer"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px] max-h-[300px]">
          {messages.length === 0 ? (
            <div className="text-center text-white/40 text-sm py-8">
              <p>{teamInfo.emoji}</p>
              <p>No messages yet</p>
              <p className="text-xs mt-2">Be the first to rally your team!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`
                  p-2 rounded-lg text-sm
                  ${msg.sender === playerAddress 
                    ? `${teamInfo.bg} border border-white/10 ml-4` 
                    : 'bg-white/5 mr-4'
                  }
                  ${msg.type === 'immortal' ? 'ring-1 ring-yellow-500/50' : ''}
                `}
              >
                {/* Sender info */}
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-white/50">
                    {msg.sender === playerAddress ? 'You' : formatAddress(msg.sender)}
                  </span>
                  <div className="flex items-center gap-2">
                    {msg.type === 'immortal' && (
                      <span className="text-yellow-500 text-xs" title="Immortalized message">
                        ⭐
                      </span>
                    )}
                    <span className="text-xs text-white/30">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
                
                {/* Message content */}
                <p className="text-white/90">{msg.content}</p>
                
                {/* Immortalize button (for own messages) */}
                {msg.sender === playerAddress && msg.type !== 'immortal' && signMessage && (
                  <button
                    onClick={() => handleImmortalize(msg.content)}
                    className="text-xs text-yellow-500/50 hover:text-yellow-500 mt-1 transition-colors"
                    title="Make this message permanent (costs PAW)"
                  >
                    ⭐ Immortalize
                  </button>
                )}
              </motion.div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Error display */}
        {error && (
          <div className="px-3 py-2 bg-red-500/20 text-red-400 text-xs">
            {error}
          </div>
        )}

        {/* Input */}
        <div className="p-3 border-t border-white/10">
          {playerAddress ? (
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Rally your team..."
                maxLength={200}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30"
                disabled={isSending}
              />
              <button
                onClick={handleSend}
                disabled={!newMessage.trim() || isSending}
                className={`
                  px-4 py-2 rounded-lg font-medium text-sm transition-all
                  ${newMessage.trim() && !isSending
                    ? `bg-gradient-to-r ${teamInfo.color} text-white hover:opacity-90`
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                  }
                `}
              >
                {isSending ? '...' : 'Send'}
              </button>
            </div>
          ) : (
            <p className="text-center text-white/40 text-sm">
              Connect wallet to chat
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Compact chat toggle button
export const ChatToggle: React.FC<{
  team: number;
  unreadCount?: number;
  onClick: () => void;
}> = ({ team, unreadCount = 0, onClick }) => {
  const teamInfo = TEAM_INFO[team as keyof typeof TEAM_INFO] || TEAM_INFO[0];

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        relative p-3 rounded-full bg-gradient-to-r ${teamInfo.color}
        shadow-lg hover:shadow-xl transition-shadow
      `}
    >
      <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
      
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-bold">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </motion.button>
  );
};

export default TeamChat;
